import { useEffect, useState } from 'react';
import { DailyTask } from '../types/goals';
import { PaginationData } from '../pages/GoalsPage';
import { dailyTasksAPI } from '../lib/api/task';

export interface UseDailyTasksState {
    tasks: DailyTask[];
    stats: {
        total: number;
        completedTasks: number;
        overdueTasks: number;
    } | null;
    pagination: PaginationData | null;
    isLoading: boolean;
    error: string | null;
}

export interface UseDailyTasksFilters {
    page?: number;
    limit?: number;
    goalId?: string;
    milestoneId?: string;
    isCompleted?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    // Puedes agregar más filtros si es necesario
}

export function useDailyTasks(initialFilters?: UseDailyTasksFilters) {
    const [state, setState] = useState<UseDailyTasksState>({
        tasks: [],
        stats: null,
        pagination: null,
        isLoading: false,
        error: null
    });

    const defaultFilters: UseDailyTasksFilters = {
        page: 1,
        limit: 10,
        sortBy: 'createdAt',
        sortOrder: 'desc'
    };

    const [filters, setFilters] = useState<UseDailyTasksFilters>({
        ...defaultFilters,
        ...initialFilters
    });

    const fetchTasks = async (newFilters?: UseDailyTasksFilters) => {
        try {
            setState(prev => ({ ...prev, isLoading: true, error: null }));
            const response = await dailyTasksAPI.getTasks(newFilters || filters);

            setState(prev => ({
                ...prev,
                tasks: response.tasks || [],
                pagination: response.pagination || null,
                stats: response.stats || null,
                isLoading: false
            }));
        } catch (err) {
            setState(prev => ({
                ...prev,
                tasks: [],
                stats: null,
                pagination: null,
                error: err instanceof Error ? err.message : 'Failed to fetch tasks',
                isLoading: false
            }));
            throw err;
        }
    };

    const fetchTaskDetails = async (taskId: string) => {
        try {
            setState(prev => ({ ...prev, isLoading: true, error: null }));
            const task = await dailyTasksAPI.getTask(taskId);
            // Aquí podrías actualizar el estado con detalles de la tarea si lo requieres.
            setState(prev => ({ ...prev, isLoading: false }));
            return task;
        } catch (err) {
            setState(prev => ({
                ...prev,
                error: err instanceof Error ? err.message : 'Failed to fetch task details',
                isLoading: false
            }));
            throw err;
        }
    };

    const createTask = async (data: {
        goalId: string;
        milestoneId: string;
        title: string;
        description?: string;
        date: Date;
        isCompleted?: boolean;
    }) => {
        try {
            setState(prev => ({ ...prev, isLoading: true, error: null }));
            const response = await dailyTasksAPI.createTask(data);
            setState(prev => ({
                ...prev,
                tasks: [response.task, ...prev.tasks],
                stats: response.stats, // si el endpoint devuelve stats actualizadas
                isLoading: false
            }));
            return response;
        } catch (err) {
            setState(prev => ({
                ...prev,
                error: err instanceof Error ? err.message : 'Failed to create task',
                isLoading: false
            }));
            throw err;
        }
    };

    const updateTask = async (taskId: string, updates: Partial<DailyTask>) => {
        try {
            setState(prev => ({ ...prev, isLoading: true, error: null }));
            const updatedTask = await dailyTasksAPI.updateTask(taskId, updates);
            setState(prev => ({
                ...prev,
                tasks: prev.tasks.map(task => task._id === taskId ? updatedTask : task),
                isLoading: false
            }));
            return updatedTask;
        } catch (err) {
            setState(prev => ({
                ...prev,
                error: err instanceof Error ? err.message : 'Failed to update task',
                isLoading: false
            }));
            throw err;
        }
    };

    const deleteTask = async (taskId: string) => {
        try {
            setState(prev => ({ ...prev, isLoading: true, error: null }));
            await dailyTasksAPI.deleteTask(taskId);
            setState(prev => ({
                ...prev,
                tasks: prev.tasks.filter(task => task._id !== taskId),
                isLoading: false
            }));
        } catch (err) {
            setState(prev => ({
                ...prev,
                error: err instanceof Error ? err.message : 'Failed to delete task',
                isLoading: false
            }));
            throw err;
        }
    };

    const toggleTaskCompletion = async (taskId: string) => {
        try {
            setState(prev => ({ ...prev, isLoading: true, error: null }));
            const updatedTask = await dailyTasksAPI.toggleTaskCompletion(taskId);
            setState(prev => ({
                ...prev,
                tasks: prev.tasks.map(task => task._id === taskId ? updatedTask : task),
                isLoading: false
            }));
            return updatedTask;
        } catch (err) {
            setState(prev => ({
                ...prev,
                error: err instanceof Error ? err.message : 'Failed to toggle task completion',
                isLoading: false
            }));
            throw err;
        }
    }

    const updateFilters = (newFilters: Partial<UseDailyTasksFilters>) => {
        const updatedFilters = { ...filters, ...newFilters, page: 1 };
        setFilters(updatedFilters);
        fetchTasks(updatedFilters);
    };

    useEffect(() => {
        fetchTasks(filters);
    }, []);

    return {
        ...state,
        filters,
        fetchTasks,
        fetchTaskDetails,
        createTask,
        updateTask,
        deleteTask,
        updateFilters,
        toggleTaskCompletion
    };
}
